import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true
});

// Utilidad para obtener cookies de CSRF
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Lógica de Logout forzado (UserContext)
let externalForceLogout = null;
let externalLogoutCalled = false;

export const registerForceLogout = (fn) => { externalForceLogout = fn; };

function callExternalLogoutOnce() {
  if (externalLogoutCalled) return;
  externalLogoutCalled = true;
  try {
    if (typeof externalForceLogout === 'function') externalForceLogout();
  } catch (e) {
    // noop
  } finally {
    setTimeout(() => { externalLogoutCalled = false; }, 5000);
  }
}

// ----- INTERCEPTOR: REQUEST (CSRF) -----
API.interceptors.request.use((config) => {
  const isRefreshPath = config.url && config.url.includes('/auth/token/refresh');
  const tokenName = isRefreshPath ? 'csrf_refresh_token' : 'csrf_access_token';
  
  const csrfToken = getCookie(tokenName);
  if (csrfToken) {
    config.headers['X-CSRF-TOKEN'] = csrfToken;
  }
  
  return config;
}, (err) => Promise.reject(err));

// ----- INTERCEPTOR: RESPONSE (REFRESH TOKEN AUTOMÁTICO) -----
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, result) => {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(result)));
  failedQueue = [];
};

API.interceptors.response.use(
  res => res,
  async (err) => {
    const originalRequest = err.config;
    const status = err.response?.status;

    if (status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(err);
    }

    if (originalRequest.url.includes('/auth/token/refresh')) {
      callExternalLogoutOnce();
      return Promise.reject(err);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: () => resolve(API(originalRequest)),
          reject: (e) => reject(e)
        });
      });
    }

    isRefreshing = true;

    return new Promise(async (resolve, reject) => {
      try {
        const csrfRefresh = getCookie('csrf_refresh_token');
        const config = { withCredentials: true };
        if (csrfRefresh) config.headers = { 'X-CSRF-TOKEN': csrfRefresh };

        await refreshToken(config);
        processQueue(null, true);
        resolve(API(originalRequest));
      } catch (refreshError) {
        processQueue(refreshError, false);
        callExternalLogoutOnce();
        reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    });
  }
);

// ----------------------- ENDPOINTS ----------------------- 

// Auth
export const login = ({ email, password }) => API.post('/auth/login', { email, password });
export const logout = () => API.post('/auth/logout');
export const refreshToken = (config = {}) => API.post('/auth/token/refresh', undefined, config);

// Usuarios / Perfil
export const fetchCurrentUser = (config = {}) => API.get('/usuarios/me', config);
export const getDetalles = () => API.get('/usuarios/me/detalles');
export const patchPassword = (payload) => API.patch('/usuarios/me', payload);
export const getUsers = (opts = {}) => {
  const { page = 1, per_page = 20, signal } = opts;
  return API.get('/usuarios/', { params: { page, per_page }, signal });
};
export const postUser = (userData) => API.post('/usuarios/', userData);
export const deleteUser = (usuarioId) => API.delete(`/usuarios/${usuarioId}`);
export const dashboardEmpresa = () => API.get('/empresas/me/dashboard');

// Pacientes
export const getPatients = (opts = {}) => {
  const { page = 1, per_page = 10, rut, nombre, apellido_p, apellido_m, sort = 'id_usuario', order = 'desc', signal } = opts;
  return API.get('/pacientes/', { params: { page, per_page, rut, nombre, apellido_p, apellido_m, sort, order }, signal });
};
export const getPatientById = (pacienteId) => API.get(`/pacientes/${pacienteId}`);
export const dashboardPaciente = () => API.get('/pacientes/me/dashboard');
export const postPatient = (formData) => API.post('/pacientes/', formData);
export const patchPatient = (pacienteId, patientData) => API.patch(`/pacientes/${pacienteId}`, patientData);
export const deletePatient = (pacienteId) => API.delete(`/pacientes/${pacienteId}`);

// Evaluaciones
export const getEvaluations = (opts = {}) => {
  const { page = 1, per_page = 10, sort, order, empresa, paciente, signal } = opts;
  return API.get('/evaluaciones/', { params: { page, per_page, sort, order, empresa, paciente }, signal });
};
export const getEvaPorId = (evaluacionId) => API.get(`/evaluaciones/${evaluacionId}`);
export const getVerificacionEva = (valor) => API.get(`/evaluaciones/verificar/${valor}`);
export const postEva = (pacienteId, payload) => API.post(`/pacientes/${pacienteId}/evaluaciones`, payload);
export const patchEva = (evaluacionId, payload) => API.patch(`/evaluaciones/${evaluacionId}`, payload);
export const deleteEva = (IdEva) => API.delete(`/evaluaciones/${IdEva}`);

// Exámenes
export const postExamen = (evaluacionId, tipo, payload) => API.post(`/evaluaciones/${evaluacionId}/examenes/${tipo}`, payload);
export const getExamenById = (evaId, examenId) => API.get(`evaluaciones/${evaId}/examenes/${examenId}`);
export const patchExamenById = (evaId, examenId, payload) => API.patch(`evaluaciones/${evaId}/examenes/${examenId}`, payload);
export const deleteExamen = (evaluacionId, id_examen) => API.delete(`/evaluaciones/${evaluacionId}/examenes/${id_examen}`);
export const getDatosExamenes = (eva_id) => API.get(`evaluaciones/${eva_id}/examenes`);

// PDF
export const listarPDFsEva = (page = 1, per_page = 20, sort = 'fecha', order = 'asc') => 
  API.get(`/evaluaciones/pdfs?page=${page}&per_page=${per_page}&sort=${sort}&order=${order}`);
export const generarPDF = (id_eva) => API.post(`/evaluaciones/${id_eva}/pdf`);
export const eliminarPDF = (id_eva, id_pdf) => API.delete(`/evaluaciones/${id_eva}/pdf/${id_pdf}`);
///ANEXOS
export const getAnexosEva = (id_eva) => API.get(`/evaluaciones/${id_eva}/anexos`);
export const postAnexoEva = (id_eva, formData) => API.post(`/evaluaciones/${id_eva}/anexos`, formData, {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});
export const getAnexoEvaById = (id_eva,id_anexo) => API.get(`/evaluaciones/${id_eva}/anexos/${id_anexo}`);
export const patchAnexoEvaById = (id_eva,id_anexo,payload) => API.patch(`/evaluaciones/${id_eva}/anexos/${id_anexo}`,payload);
export const deleteAnexoEva = (id_eva,id_anexo) => API.delete(`/evaluaciones/${id_eva}/anexos/${id_anexo}`);
export const obtenerAnexoBlobUrl = async (idEva, idAnexo) => {
  const response = await API.get(`/evaluaciones/${idEva}/anexos/${idAnexo}`, {
    responseType: 'blob', // Importante para recibir archivos en binario
  });
  
  // Crea una URL de objeto en memoria segura protegida por el navegador
  const blob = new Blob([response.data], { type: response.headers['content-type'] });
  return URL.createObjectURL(blob);
};
// ==================== FUNCIONES DE ANEXOS ====================

/**
 * Helper para abrir cualquier URL de archivo en pestaña nueva
 */
export const abrirArchivoURL = (relativeOrAbsoluteUrl) => {
  if (!relativeOrAbsoluteUrl) return;
  const fullUrl = relativeOrAbsoluteUrl.startsWith('http')
    ? relativeOrAbsoluteUrl
    : `${API.defaults.baseURL}${relativeOrAbsoluteUrl}`;
  window.open(fullUrl, '_blank', 'noopener,noreferrer');
};

/**
 * Helper para abrir un anexo específico
 */
export const abrirAnexoEva = (idEva, idAnexo) => {
  if (!idEva || !idAnexo) return;
  const url = `${API.defaults.baseURL}/evaluaciones/${idEva}/anexos/${idAnexo}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};
// Empresas
export const getCompanies = (opts = {}) => {
  const { page = 1, per_page = 20, signal } = opts;
  return API.get('/empresas/', { params: { page, per_page }, signal });
};
export const getCompanyById = (empresaId) => API.get(`/empresas/${empresaId}`);
export const postCompany = (payload) => API.post('/empresas/', payload);
export const patchCompanyById = (empresaId, payload) => API.patch(`/empresas/${empresaId}`, payload);
export const deleteCompany = (empresaId) => API.delete(`/empresas/${empresaId}`);
export const postContactoEmpresa = (empresaId, payload) => API.post(`/empresas/${empresaId}/contacto_empresa`, payload);
export const patchContactoEmpresa= (empresaId,payload,contactoId) => API.patch(`/empresas/${empresaId}/contacto_empresa/${contactoId}`,payload);
export const deleteContactoEmpresa = (empresaId,contactoId) =>API.delete(`/empresas/${empresaId}/contacto_empresa/${contactoId}`);
// Baterías
export const getBat = () => API.get('/baterias/');
export const getBatById = (bateriaId) => API.get(`/baterias/${bateriaId}`);
export const postBateria = (nombre) => API.post('/baterias/', { nombre });
export const deleteBateria = (bateriaId) => API.delete(`/baterias/${bateriaId}`);
export const getTipoExamen = () => API.get('/tipos_examen/');
export const asociarExamYBat = (payload, bateria_id) => API.patch(`/baterias/${bateria_id}/tipos_examen`, payload);

// Conclusión Médica
export const getConclusionMedica = (eva_id) => API.get(`evaluaciones/${eva_id}/conclusion_medica`);
export const postConclusionMedica = (eva_id, payload) => API.post(`evaluaciones/${eva_id}/conclusion_medica`, payload);
export const patchConclusionMedica = (eva_id, payload) => API.patch(`evaluaciones/${eva_id}/conclusion_medica`, payload);

// Firmas (firma escaneada del médico evaluador, asociada por nombre_medico)
// Fuente de verdad de "qué médicos existen": solo los que ya tienen una firma cargada.
export const getNombresMedico = () => API.get('/firmas/medicos');
export const getFirmas = (opts = {}) => {
  const { page = 1, per_page = 20, nombre_medico, signal } = opts;
  return API.get('/firmas/', { params: { page, per_page, nombre_medico }, signal });
};
export const postFirma = (formData) => API.post('/firmas/', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const patchFirma = (idFirma, payload) => API.patch(`/firmas/${idFirma}`, payload);
export const deleteFirma = (idFirma) => API.delete(`/firmas/${idFirma}`);
export const obtenerFirmaBlobUrl = async (idFirma) => {
  const response = await API.get(`/firmas/${idFirma}/archivo`, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: response.headers['content-type'] });
  return URL.createObjectURL(blob);
};

export default API;